import { useState } from "react";
import { cn } from "@/lib/utils";
import { INTEGRATION_COLORS } from "@/data/integration-colors";

interface IntegrationLogoProps {
  /**
   * Integration slug. Used to look up the fallback brand color, and to
   * build the default `/logos/<slug>.svg` URL when `src` isn't given.
   */
  slug: string;
  name: string;
  /**
   * Explicit URL — typically `row.logo` off an `IntegrationCatalogRow`,
   * which the kit resolves to `/logos/<slug>.<ext>`. Wins over the
   * slug-derived default when set.
   */
  src?: string;
  /** Pixel size. */
  size?: number;
  className?: string;
  /** Show brand-coloured halo on hover. */
  withHalo?: boolean;
}

/**
 * Renders an integration's logo. Defaults to `/logos/<slug>.svg`;
 * pass `src` to use an explicit URL (e.g. from the catalog row).
 * Falls back to the slug's first letter on a brand-coloured tile if
 * the image fails to load.
 */
export function IntegrationLogo({
  slug,
  name,
  src,
  size = 32,
  className,
  withHalo = false,
}: IntegrationLogoProps) {
  const [failed, setFailed] = useState(false);
  const brand = INTEGRATION_COLORS[slug] ?? "#52525B";
  const url = src ?? `/logos/v3/${slug}.svg`;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {!failed ? (
        <img
          src={url}
          alt={`${name} logo`}
          width={size}
          height={size}
          loading="lazy"
          onError={() => setFailed(true)}
          className="block object-contain"
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          style={{ background: brand, width: size, height: size }}
          className="flex items-center justify-center rounded-md text-[10px] font-bold text-white"
        >
          {name[0]?.toUpperCase() ?? "?"}
        </span>
      )}
      {withHalo && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-30"
          style={{ background: brand }}
        />
      )}
    </span>
  );
}
